import type { DayStatus } from "@shared/trustLabels";

/**
 * Drafts tab layout decisions (pure, testable).
 *
 * Personal-workspace streamlining: a personal Drafts week is a private log,
 * so it should not repeat the same "1 update captured — ready to draft" line
 * three times (Today card + This-week card + the today row in Daily Receipts).
 *
 * `showTodayDraftCard` decides whether the standalone "Today" card renders:
 *   • TEAM workspace — always shows on the current week (unchanged behaviour:
 *     it's the focal daily action and the team view stays as-is).
 *   • PERSONAL workspace — hidden when today is ALREADY a row in the Daily
 *     Receipts list below (a working day, or a rest day that carries a
 *     captured note), so the same day isn't shown twice. On a non-working
 *     rest day with no capture (today is NOT in that list) it still shows,
 *     so today stays reachable.
 *
 * The card never shows on a past/future week regardless of workspace — it is
 * about *today*.
 */
export function showTodayDraftCard(a: {
  isCurrentWeek: boolean;
  isPersonal: boolean;
  /** Whether today appears as a row in the Daily Receipts list (working day
   *  or a rest day with a captured weekend note). */
  todayInDailyList: boolean;
}): boolean {
  if (!a.isCurrentWeek) return false;
  if (a.isPersonal && a.todayInDailyList) return false;
  return true;
}

/**
 * Whether the receipt editor shows the "Unshare" (retract-from-team-feed)
 * control. Retract only means something in a TEAM workspace: a personal
 * receipt is never shared, so there is nothing to unshare. Otherwise it's
 * offered only while the receipt is currently shared — approved and not
 * already retracted.
 */
export function showEditorUnshare(a: {
  isPersonal: boolean;
  approvedAt: unknown;
  retractedAt: unknown;
}): boolean {
  return !a.isPersonal && a.approvedAt != null && a.retractedAt == null;
}

/** Minimal shape of the weekly receipt the card status needs. */
type WeeklyLike = { approvedAt: unknown; retractedAt: unknown; status: string } | null | undefined;

/**
 * Display status for the "This week" DraftReceiptCard, derived from the server
 * weekly row + the server `weeklyStale` freshness flag.
 *
 * CRITICAL: the post-approval states — `needs_update` ("Changes not shared")
 * and `update_available` — imply an APPROVED, team-shared snapshot (the card
 * offers "Unshare"/"Re-approve" for them). `weeklyStale` is a pure FRESHNESS
 * flag (content older than this week's inputs) and is true even for a
 * NEVER-APPROVED draft (e.g. a daily got approved after the weekly was
 * drafted). Mapping a stale-but-never-approved weekly to `needs_update` made
 * the card offer "Unshare" on a receipt that was never shared — and clicking it
 * errored "Couldn't unshare" (retract refuses with no approved snapshot). So
 * `needs_update` is gated on `approvedAt != null`, mirroring the server's
 * daily day-status (server/weekStatus.ts). A never-approved stale weekly is
 * just `ready` (Approve & share; no Unshare).
 */
export function weeklyCardStatus(weekly: WeeklyLike, weeklyStale: boolean): DayStatus {
  if (!weekly) return "not_started";
  if (weekly.approvedAt != null && weekly.retractedAt != null) return "retracted";
  if (weekly.status === "approved" || weekly.status === "published") return "approved";
  if (weekly.approvedAt != null && weeklyStale) return "needs_update";
  return "ready";
}
